import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api";
import Source_Book from "../assets/UserIcons/Source Book1.svg";
import { showErrorToast } from "../utils/toastUtils";
import ConfirmModal from "./ConfirmModal";

const normalizeFilename = (value) => String(value ?? "").trim();
const normalizeFilenameKey = (value) =>
  normalizeFilename(value).toLowerCase();

const normalizeGroups = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((g) => {
      const id = g?.id ?? g?.department_id ?? g?._id ?? g?.group_id;
      const name = g?.name ?? g?.department_name ?? g?.group_name;
      if (id == null || !name) return null;
      return { id: String(id), name: String(name) };
    })
    .filter(Boolean);
};

const KnowledgeBaseDropdown = ({
  knowledgeSearchEnabled,
  setKnowledgeSearchEnabled,
  groups,
  setGroups,
  selectedGroups,
  setSelectedGroups,
  selectedFiles,
  setSelectedFiles,
  selectedKnowledgeContexts = [],
  setSelectedKnowledgeContexts,
  knowledgeContextSummary = {},
  setKnowledgeContextSummary,
  updateUserPreferences,
  selectedModelId,
  selectedMode,
  triggerChatReset,
  onSessionCreated,
}) => {
  const location = useLocation();
  const normalizeKbEntry = (value) => {
    if (typeof value === "string") {
      const filename = String(value).trim();
      return filename ? { filename, document_id: null } : null;
    }
    if (value && typeof value === "object") {
      const filename = String(value.filename ?? value.name ?? "").trim();
      if (!filename) return null;
      const rawDocId = value.document_id ?? value.documentId ?? null;
      const document_id =
        rawDocId == null || String(rawDocId).trim() === ""
          ? null
          : String(rawDocId);
      return { filename, document_id };
    }
    return null;
  };

  const selectedFileEntriesRaw = Array.isArray(selectedFiles) ? selectedFiles : [];
  const selectedFileEntries = selectedFileEntriesRaw
    .map(normalizeKbEntry)
    .filter(Boolean);
  const selectedFilesArray = selectedFileEntries.map((e) => e.filename);
  const selectedGroupsArray = Array.isArray(selectedGroups)
    ? selectedGroups
    : [];
  const selectedFilesLength = selectedFilesArray.length;
  const selectedGroupsLength = selectedGroupsArray.length;
  const selectedContextsArray = Array.isArray(selectedKnowledgeContexts)
    ? selectedKnowledgeContexts
    : [];
  const selectedFoldersArray = selectedContextsArray.filter(
    (ctx) => ctx?.type === "folder",
  );
  const selectedFoldersLength = selectedFoldersArray.length;
  const selectedFolderKeySet = useMemo(
    () =>
      new Set(
        selectedFoldersArray
          .map((ctx) => String(ctx.folder_id || ctx.id || "").trim())
          .filter(Boolean),
      ),
    [selectedFoldersArray],
  );

  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [customPool, setCustomPool] = useState([]);
  const [showEnableGlobalModal, setShowEnableGlobalModal] = useState(false);
  const [enableGlobalBusy, setEnableGlobalBusy] = useState(false);
  const prevSelectedKeyCountRef = useRef(null);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setCustomOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Auto-open the KB dropdown when KB files get selected from the chat message
  // (RagSourcesPill), so the user immediately sees Custom selections checked.
  useEffect(() => {
    const pathname = String(location?.pathname || "");
    if (!pathname.startsWith("/user/new")) return;

    const keys = new Set(
      (selectedFilesArray || []).map(normalizeFilenameKey).filter(Boolean),
    );
    selectedFolderKeySet.forEach((key) => keys.add(`folder:${key}`));
    const nextCount = keys.size;

    // Ignore the first run (mount) to avoid popping open on page load/refresh.
    if (prevSelectedKeyCountRef.current === null) {
      prevSelectedKeyCountRef.current = nextCount;
      return;
    }

    const prevCount = prevSelectedKeyCountRef.current;
    prevSelectedKeyCountRef.current = nextCount;

    // Only auto-open when a file was added.
    if (nextCount > 0 && nextCount > prevCount) {
      setOpen(true);
      setCustomOpen(true);
    }
  }, [location?.pathname, selectedFilesArray, selectedFolderKeySet]);

  // If we just created a new chat session from a KB-driven action (e.g. "New Chat" from RagSources),
  // auto-open the dropdown on the next screen.
  useEffect(() => {
    const pathname = String(location?.pathname || "");
    if (!pathname.startsWith("/user/new")) return;

    const keys = new Set(
      (selectedFilesArray || []).map(normalizeFilenameKey).filter(Boolean),
    );
    selectedFolderKeySet.forEach((key) => keys.add(`folder:${key}`));
    if (keys.size === 0) return;

    let flag = null;
    try {
      flag = sessionStorage.getItem("kb_auto_open_dropdown");
    } catch {}

    if (flag === "1") {
      setOpen(true);
      setCustomOpen(true);
      prevSelectedKeyCountRef.current = keys.size;
      try {
        sessionStorage.removeItem("kb_auto_open_dropdown");
      } catch {}
    }
  }, [location?.pathname, selectedFilesArray, selectedFolderKeySet]);

  const fetchGroups = async () => {
    if (loadingGroups) return;
    setLoadingGroups(true);
    try {
      let res;
      try {
        res = await api.get(
          `${process.env.REACT_APP_API_URL}/departments/accessible`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (e) {
        // Fallback for older backends
        res = await api.get(`${process.env.REACT_APP_API_URL}/departments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setGroups(normalizeGroups(res.data));
    } catch (err) {
      console.error("Failed to fetch groups:", err);
      showErrorToast("Failed to load groups");
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!groups || groups.length === 0) fetchGroups();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // If files are already selected, open the Custom submenu by default
  // so users can immediately see what's selected (mirrors Document History behavior).
  useEffect(() => {
    if (!open) return;
    if (knowledgeSearchEnabled) {
      setCustomOpen(false);
      return;
    }
    if (selectedFilesLength > 0 || selectedFoldersLength > 0) setCustomOpen(true);
  }, [open, knowledgeSearchEnabled, selectedFilesLength, selectedFoldersLength]);

  const resetSelections = () => {
    setSelectedGroups?.([]);
    setSelectedFiles?.([]);
    setSelectedKnowledgeContexts?.([]);
    setKnowledgeContextSummary?.({});
    setCustomOpen(false);
    setCustomPool([]);
  };

  const handleGlobalToggle = async () => {
    if (!knowledgeSearchEnabled) {
      setOpen(false);
      setCustomOpen(false);
      setShowEnableGlobalModal(true);
      return;
    }

    // Uncheck: no popup
    resetSelections();
    setKnowledgeSearchEnabled(false);
    await updateUserPreferences?.({
      preferences: { chat: { knowledge_search_enabled: false } },
    });
  };

  const confirmEnableGlobal = async () => {
    if (enableGlobalBusy) return;
    setEnableGlobalBusy(true);
    try {
      resetSelections();
      setKnowledgeSearchEnabled(true);
      await updateUserPreferences?.({
        preferences: { chat: { knowledge_search_enabled: true } },
      });
      setShowEnableGlobalModal(false);
    } finally {
      setEnableGlobalBusy(false);
    }
  };

  const toggleGroup = (groupId) => {
    setSelectedGroups((prev) => {
      const set = new Set(prev.map(String));
      if (set.has(String(groupId))) set.delete(String(groupId));
      else set.add(String(groupId));
      return Array.from(set);
    });
  };

  const toggleFile = (source) => {
    const filename = normalizeFilename(source?.filename);
    if (!filename) return;
    setSelectedFiles((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const targetKey = normalizeFilenameKey(filename);
      const map = new Map();
      for (const v of prevArr) {
        const entry = normalizeKbEntry(v);
        const k = normalizeFilenameKey(entry?.filename);
        if (!k) continue;
        if (!map.has(k)) map.set(k, entry);
      }
      if (map.has(targetKey)) map.delete(targetKey);
      else {
        const existing = map.get(targetKey) || null;
        map.set(targetKey, {
          filename,
          document_id: existing?.document_id ?? null,
        });
      }
      return Array.from(map.values()).filter(Boolean);
    });
  };

  // Custom submenu shows only currently-selected files (no "seen" history).
  useEffect(() => {
    const incoming = selectedFilesArray
      .map(normalizeFilename)
      .filter(Boolean);

    const map = new Map();
    for (const f of incoming) {
      const k = normalizeFilenameKey(f);
      if (!k) continue;
      if (!map.has(k)) map.set(k, f);
    }

    setCustomPool(Array.from(map.values()));
  }, [selectedFiles]);

  const selectedFilesKeySet = useMemo(() => {
    return new Set(
      selectedFilesArray.map(normalizeFilenameKey).filter(Boolean),
    );
  }, [selectedFiles]);

  const customPoolNormalized = useMemo(() => {
    return (customPool || []).map(normalizeFilename).filter(Boolean);
  }, [customPool]);

  const customPoolKeys = useMemo(() => {
    return (customPoolNormalized || []).map(normalizeFilenameKey).filter(Boolean);
  }, [customPoolNormalized]);

  const allCustomSelected =
    customPoolKeys.length > 0 &&
    customPoolKeys.every((k) => selectedFilesKeySet.has(k));
  const someCustomSelected = customPoolKeys.some((k) =>
    selectedFilesKeySet.has(k),
  );

  const customSelectAllRef = useRef(null);
  const customIndicatorRef = useRef(null);

  useEffect(() => {
    if (customSelectAllRef.current) {
      customSelectAllRef.current.indeterminate =
        someCustomSelected && !allCustomSelected;
    }
    if (customIndicatorRef.current) {
      customIndicatorRef.current.indeterminate =
        someCustomSelected && !allCustomSelected;
    }
  }, [someCustomSelected, allCustomSelected]);

  const toggleSelectAllCustom = () => {
    setSelectedFiles((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const map = new Map();
      for (const v of prevArr) {
        const entry = normalizeKbEntry(v);
        const k = normalizeFilenameKey(entry?.filename);
        if (!k) continue;
        if (!map.has(k)) map.set(k, entry);
      }
      if (allCustomSelected) {
        customPoolKeys.forEach((k) => map.delete(k));
      } else {
        (customPoolNormalized || []).forEach((f) => {
          const k = normalizeFilenameKey(f);
          if (!k) return;
          const existing = map.get(k) || null;
          map.set(k, {
            filename: f,
            document_id: existing?.document_id ?? null,
          });
        });
      }
      return Array.from(map.values()).filter(Boolean);
    });
  };

  const handleCreateSession = async () => {
    if (!selectedModelId) {
      showErrorToast("Select a model first");
      return;
    }

    setCreatingSession(true);
    try {
      const res = await api.post(
          `${process.env.REACT_APP_API_URL}/chat/new-session`,
          {
            model_id: selectedModelId,
            mode: selectedMode,
            knowledge_search_enabled: Boolean(
              knowledgeSearchEnabled ||
                selectedGroupsLength > 0 ||
                selectedFilesLength > 0 ||
                selectedFoldersLength > 0,
            ),
            group_ids: selectedGroupsArray,
            knowledge_base_files: selectedFileEntries,
            knowledge_contexts: selectedContextsArray,
          },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const sessionId = res.data?.session_id;
      if (!sessionId) throw new Error("Missing session_id");

      try {
        sessionStorage.setItem("kb_auto_open_dropdown", "1");
        sessionStorage.setItem("kb_keep_selection_for_next_session", "1");
      } catch {}

      onSessionCreated?.(sessionId);
      triggerChatReset?.();
      setOpen(false);
      setCustomOpen(false);
    } catch (err) {
      console.error("Failed to create session:", err);
      showErrorToast("Failed to create session");
    } finally {
      setCreatingSession(false);
    }
  };

  const customChecked = selectedFilesLength > 0 || selectedFoldersLength > 0;
  const kbHasAnySelection =
    Boolean(knowledgeSearchEnabled) ||
    selectedGroupsLength > 0 ||
    selectedFilesLength > 0 ||
    selectedFoldersLength > 0;

  return (
    <div className="kb-dropdown" ref={containerRef}>
      <button
        className={`btn-link p-0 pill-icon-btn hi border-0 d-none d-sm-flex ${
          kbHasAnySelection ? "active" : ""
        }`}
        title="Knowledge Repository"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((p) => !p);
          if (!open) setCustomOpen(false);
        }}
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          backgroundColor: "#F0F0F0",
          alignItems: "center",
          justifyContent: "center",
          padding: "0",
          fontSize: "18px",
        }}
        type="button"
      >
        <img src={Source_Book} alt="knowledge" />
      </button>

      {open && (
        <div
          className="kb-panels"
          role="dialog"
          aria-label="Knowledge base options"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="kb-row">
            <label className="kb-check">
              <input
                type="checkbox"
                checked={knowledgeSearchEnabled}
                className="doc-kb-checkbox"
                onChange={handleGlobalToggle}
              />
              <span>Global</span>
            </label>
          </div>

          <div className={`kb-section ${knowledgeSearchEnabled ? "kb-disabled" : ""}`}>
            <div className="kb-section-title">Groups</div>
            <div className="kb-scroll">
              {loadingGroups ? (
                <div className="kb-muted">Loading…</div>
              ) : groups.length === 0 ? (
                <div className="kb-muted">No groups</div>
              ) : (
                groups.map((g) => (
                  <label key={g.id} className="kb-check kb-item">
                    <input
                      type="checkbox"
                      disabled={knowledgeSearchEnabled}
                       className="doc-kb-checkbox"
                      checked={selectedGroups.map(String).includes(String(g.id))}
                      onChange={() => toggleGroup(g.id)}
                    />
                    <span title={g.name} className="kb-text-truncate">
                      {g.name}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className={`kb-row kb-custom ${knowledgeSearchEnabled ? "kb-disabled" : ""}`}>
            <label className="kb-check">
              <input
                ref={customIndicatorRef}
                type="checkbox"
                disabled={knowledgeSearchEnabled}
                 className="doc-kb-checkbox"
                checked={customChecked}
                onChange={() => {
                  // Don't clear selections when clicking the Custom checkbox.
                  // This checkbox is an indicator; file removal happens in the submenu list.
                  setCustomOpen(true);
                }}
              />
              <span>Custom</span>
            </label>

            <button
              type="button"
              className="kb-caret"
              disabled={knowledgeSearchEnabled}
              aria-label="Toggle custom files"
              onClick={() => setCustomOpen((p) => !p)}
            >
              <i
                className={`bi bi-chevron-right ${customOpen ? "kb-rotate" : ""}`}
              ></i>
            </button>

            {customOpen && (
              <div className="kb-nested">
                <div className="kb-nested-title">Custom files</div>
                <div className="kb-scroll">
                  {customPool.length === 0 ? (
                    selectedFoldersLength === 0 ? (
                      <div className="kb-muted">
                        No file selected.
                      </div>
                    ) : null
                  ) : (
                    <>
                      <label className="kb-check kb-item">
                      <input
                        ref={customSelectAllRef}
                        type="checkbox"
                        checked={allCustomSelected}
                        onChange={toggleSelectAllCustom}
                         className="doc-kb-checkbox"
                      />
                      <span>
                        {allCustomSelected ? "Deselect all" : "Select all"}
                      </span>
                    </label>

                    <ul className="kb-file-list">
                      {customPool.map((filename) => (
                        <li key={filename} className="kb-file-item">
                          <input
                            type="checkbox"
                            checked={selectedFilesKeySet.has(
                              normalizeFilenameKey(filename),
                            )}
                            onChange={() => toggleFile({ filename })}
                          />
                          <span className="kb-text-truncate" title={filename}>
                            📄 {filename}
                          </span>
                        </li>
                      ))}
                    </ul>
                    </>
                  )}
                  {selectedFoldersLength > 0 && (
                    <ul className="kb-file-list">
                      {selectedFoldersArray.map((ctx) => {
                        const folderId = String(ctx.folder_id || ctx.id || "");
                        const ownFileCount = Number(ctx.file_count);
                        const fallbackFileCount =
                          selectedFoldersLength === 1
                            ? Number(knowledgeContextSummary?.file_count)
                            : NaN;
                        const fileCount = Number.isFinite(ownFileCount)
                          ? ownFileCount
                          : fallbackFileCount;
                        return (
                          <li
                            key={`folder-${folderId}`}
                            className="kb-file-item"
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={() => {
                                setSelectedKnowledgeContexts?.((prev) =>
                                  (Array.isArray(prev) ? prev : []).filter(
                                    (item) =>
                                      String(item.folder_id || item.id || "") !==
                                      folderId,
                                  ),
                                );
                                setKnowledgeContextSummary?.({});
                              }}
                            />
                            <span
                              className="kb-text-truncate"
                              title={ctx.display_name || "Folder"}
                            >
                              Folder: {ctx.display_name || "Folder"}
                              {Number.isFinite(fileCount) && fileCount > 0
                                ? ` (${fileCount} indexed files)`
                                : ""}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {false && customOpen && (
            <div className="kb-nested">
              <div className="kb-nested-title">Custom files</div>
              <div className="kb-scroll">
                {selectedFilesLength === 0 ? (
                  <div className="kb-muted">
                    Select files from Knowledge Base Source
                  </div>
                ) : (
                  <ul className="kb-file-list">
                    {selectedFilesArray.map((filename) => (
                      <li key={filename} className="kb-file-item">
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() => toggleFile({ filename })}
                           className="doc-kb-checkbox"
                        />
                        <span className="kb-text-truncate" title={filename}>
                          📄 {filename}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* <div className="kb-actions">
            <button
              type="button"
              className="kb-new-chat-btn"
              disabled={creatingSession}
              onClick={handleCreateSession}
              title="Create a new chat session with current selections"
            >
              {creatingSession ? "Creating…" : "New Chat"}
            </button>
          </div> */}
        </div>
      )}

      <ConfirmModal
        open={showEnableGlobalModal}
        iconSrc={Source_Book}
        iconAlt="Global"
        message="Enabling Global mode will clear the currently selected groups and their associated files. Do you want to continue?"
        confirmText={enableGlobalBusy ? "Please wait..." : "Continue"}
        cancelText="Cancel"
        onCancel={() => {
          if (enableGlobalBusy) return;
          setShowEnableGlobalModal(false);
        }}
        onClose={() => {
          if (enableGlobalBusy) return;
          setShowEnableGlobalModal(false);
        }}
        onConfirm={confirmEnableGlobal}
        confirmDisabled={enableGlobalBusy}
        cancelDisabled={enableGlobalBusy}
      />
    </div>
  );
};

export default KnowledgeBaseDropdown;
